import type { ClockReading } from "@infoflyt/shared/timecode";
import { readClock } from "./clock";

/**
 * Siste avlesning foer appen forsvant.
 *
 * HVORFOR localStorage OG IKKE sessionStorage. Den monotone nettklokka legger
 * oektmarkoeren sin i sessionStorage med vilje — den skal forsvinne naar iOS
 * avliver webappen, slik at ankermodellen oppdager at tellingen har begynt
 * paa nytt. Se packages/monotonic-clock/src/web.ts.
 *
 * Hjerteslaget har motsatt oppgave og maa derfor ligge motsatt sted: det skal
 * nettopp OVERLEVE avlivingen, saa veggklokka kan maales over den. Uten et
 * spor paa andre siden er et appdrap og en omstart av telefonen umulig aa
 * skille, og da maa alt antas aa vaere galt. Det var feilen: varselet kom
 * flere ganger om dagen, og et varsel man ser hele tiden varsler ingenting.
 *
 * Her ligger ingenting personlig — to tall og en oekt-id — saa dette er ikke
 * innhold som skal krypteres eller slettes med produksjonen.
 */

const NOKKEL = "infoflyt.hjerteslag.v1";

/**
 * Hvor ofte slaget skrives mens appen er framme.
 *
 * Skrivingen skjer ogsaa naar appen skjules, og det er den viktige. Dette
 * intervallet er reserven for naar iOS avliver appen uten aa gi oss
 * `visibilitychange` foerst — da er maalingen like god som slaget er ferskt.
 */
const INTERVALL_MS = 15_000;

export function lesHjerteslag(): ClockReading | null {
  try {
    const raw = localStorage.getItem(NOKKEL);
    if (raw === null) return null;
    const h = JSON.parse(raw) as ClockReading;
    if (typeof h.monotonicMs !== "number" || typeof h.wallUtcMs !== "number"
      || typeof h.bootId !== "string") return null;
    return h;
  } catch {
    // Ugyldig eller utilgjengelig lagring. Da finnes det ingen maaling, og
    // `assessWallClock` svarer `unverified` — som er det aerlige svaret.
    return null;
  }
}

function skriv(): void {
  try {
    localStorage.setItem(NOKKEL, JSON.stringify(readClock()));
  } catch {
    // Privat modus eller full disk. Vurderingen blir `unverified`, ikke feil.
  }
}

/**
 * Starter skrivingen. Returnerer funksjonen som stopper den.
 *
 * Kalles én gang, etter at klokka er kalibrert — et slag skrevet foer det
 * baerer `bootId: "uncalibrated"` og kan ikke maales mot noe.
 */
export function startHjerteslag(): () => void {
  skriv();
  const id = window.setInterval(skriv, INTERVALL_MS);
  /*
   * `pagehide` i tillegg til `visibilitychange`. iOS gir ikke alltid
   * `visibilitychange` foer en webapp legges bort, og det siste slaget foer
   * appen forsvinner er det mest verdifulle vi har.
   */
  const paaVei = (): void => { if (document.visibilityState !== "visible") skriv(); };
  document.addEventListener("visibilitychange", paaVei);
  window.addEventListener("pagehide", skriv);
  return () => {
    window.clearInterval(id);
    document.removeEventListener("visibilitychange", paaVei);
    window.removeEventListener("pagehide", skriv);
  };
}
